/**
 * Suivi de la position et lissage des relevés GPS.
 *
 * Portage de `LocationKalmanFilter` et du pipeline `locationCallback` de
 * `MapManager.kt`. Un fix GPS brut est trop bruité pour piloter directement
 * la caméra : sans filtrage, la carte tremble à l'arrêt et le cap oscille.
 *
 * Limite connue sur iOS : Safari ne donne accès ni à une cadence de
 * rafraîchissement configurable ni au cap magnétique sans geste explicite de
 * l'utilisateur. Le cap est donc déduit du déplacement, ce qui le rend
 * indisponible à l'arrêt — comportement déjà retenu côté Android, où la
 * rotation est gelée sous 4 km/h.
 */

import { METERS_PER_DEGREE_LAT, haversine, normalizeBearingDelta } from './geo'

/** Au-delà, le relevé est trop imprécis pour déplacer la carte. */
export const MAX_ACCURACY_M = 50

/** Déplacement minimal pour être considéré comme réel et non comme du bruit. */
export const MIN_MOVE_DISTANCE_M = 1

/** Vitesse à partir de laquelle la carte suit le cap (environ 4 km/h). */
export const BEARING_SPEED_THRESHOLD_MS = 1.1

/** Variation de cap en deçà de laquelle la rotation est ignorée. */
export const MIN_BEARING_CHANGE_DEG = 3

/** Bruit de process du filtre, en mètres par seconde. */
const PROCESS_NOISE_MS = 3.0

export interface LocationSample {
  readonly lat: number
  readonly lon: number
  /** Altitude ellipsoïdale telle que fournie par le navigateur. */
  readonly altitude: number | null
  readonly accuracy: number
  readonly speedMs: number | null
  readonly bearing: number | null
  readonly timestamp: number
  /**
   * Ce relevé peut-il piloter la carte ?
   *
   * Faux au-delà de `MAX_ACCURACY_M` : Android alimente le HUD vitesse et
   * altitude puis **sort** avant le filtre de Kalman, l'accrochage et la cible
   * de caméra (`MapManager.kt:1828-1836`). Le portage écartait bien le relevé
   * ici, mais le remontait sans le dire, et `app.ts` le passait à la
   * navigation comme les autres : sous couvert urbain, un point à 200 m
   * déplaçait la caméra et relançait le calcul d'accrochage. L'écart vivait
   * dans le consommateur, pas dans le moteur — d'où ce drapeau, qui met la
   * décision à l'endroit qui la connaît plutôt que de recopier le seuil.
   */
  readonly trackable: boolean
}

/**
 * Filtre de Kalman scalaire appliqué séparément à la latitude et à la
 * longitude, avec une variance commune.
 *
 * Un filtre 2D complet n'apporterait rien ici : les deux axes partagent la
 * même source d'erreur, et la matrice de covariance serait diagonale.
 */
export class LocationKalmanFilter {
  #lat = 0
  #lon = 0
  #variance = Number.MAX_VALUE
  #timestamp = 0

  reset(): void {
    this.#variance = Number.MAX_VALUE
  }

  process(sample: LocationSample): { lat: number; lon: number } {
    const accuracy = Math.max(sample.accuracy, 1)
    const accuracyDeg = accuracy / METERS_PER_DEGREE_LAT
    const measurementVariance = accuracyDeg * accuracyDeg

    if (this.#variance === Number.MAX_VALUE) {
      this.#lat = sample.lat
      this.#lon = sample.lon
      this.#variance = measurementVariance
      this.#timestamp = sample.timestamp
      return { lat: this.#lat, lon: this.#lon }
    }

    const dtSeconds = (sample.timestamp - this.#timestamp) / 1000
    this.#timestamp = sample.timestamp

    // Prédiction : l'incertitude croît avec le temps écoulé depuis le dernier
    // relevé, proportionnellement à la vitesse plausible.
    if (dtSeconds > 0) {
      const qDeg = (PROCESS_NOISE_MS * dtSeconds) / METERS_PER_DEGREE_LAT
      this.#variance += qDeg * qDeg
    }

    const gain = this.#variance / (this.#variance + measurementVariance)
    this.#lat += gain * (sample.lat - this.#lat)
    this.#lon += gain * (sample.lon - this.#lon)
    this.#variance *= 1 - gain

    return { lat: this.#lat, lon: this.#lon }
  }
}

/**
 * Nature d'un échec de localisation.
 *
 * Un code plutôt qu'un message : la traduction relève de l'interface, et ce
 * module n'a pas à connaître la langue active.
 */
export type LocationErrorCode =
  | 'unsupported'
  | 'permission_denied'
  | 'unavailable'

export interface LocationTrackerOptions {
  onUpdate(sample: LocationSample): void
  onError(code: LocationErrorCode): void
}

export class LocationTracker {
  #watchId: number | null = null
  #filter = new LocationKalmanFilter()
  #previous: LocationSample | null = null
  #bearing: number | null = null

  /** Évite de répéter le même signalement à chaque relevé manqué. */
  #reportedUnavailable = false

  constructor(private readonly options: LocationTrackerOptions) {}

  get isActive(): boolean {
    return this.#watchId !== null
  }

  start(): void {
    if (this.#watchId !== null) return

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.options.onError('unsupported')
      return
    }

    // Le filtre repart de zéro : un relevé antérieur à une longue pause
    // provoquerait un saut de position au redémarrage.
    this.#filter.reset()
    this.#previous = null
    this.#reportedUnavailable = false

    this.#watchId = navigator.geolocation.watchPosition(
      (position) => this.#handlePosition(position),
      (error) => this.#handleError(error),
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 15_000,
      },
    )
  }

  stop(): void {
    if (this.#watchId === null) return
    navigator.geolocation.clearWatch(this.#watchId)
    this.#watchId = null
  }

  #handlePosition(position: GeolocationPosition): void {
    const coords = position.coords

    const raw: LocationSample = {
      lat: coords.latitude,
      lon: coords.longitude,
      altitude: coords.altitude,
      accuracy: coords.accuracy,
      speedMs: coords.speed,
      bearing: coords.heading,
      timestamp: position.timestamp,
      trackable: true,
    }

    // Un relevé très imprécis est remonté pour l'affichage de la vitesse,
    // mais ne doit pas faire bouger la carte.
    if (raw.accuracy > MAX_ACCURACY_M) {
      this.options.onUpdate({ ...raw, bearing: null, trackable: false })
      return
    }

    const filtered = this.#filter.process(raw)
    const speed = this.#computeSpeed(raw, filtered)

    // À l'arrêt, la dérive GPS ne doit pas être prise pour un déplacement.
    if (this.#previous !== null) {
      const moved = haversine(
        this.#previous.lat,
        this.#previous.lon,
        filtered.lat,
        filtered.lon,
      )
      if (moved < MIN_MOVE_DISTANCE_M && (speed ?? 0) < BEARING_SPEED_THRESHOLD_MS) {
        return
      }
    }

    const bearing = this.#computeBearing(raw, filtered, speed)

    const sample: LocationSample = {
      ...raw,
      lat: filtered.lat,
      lon: filtered.lon,
      speedMs: speed,
      bearing,
    }

    this.#previous = sample
    this.options.onUpdate(sample)
  }

  /**
   * Vitesse en mètres par seconde.
   *
   * Le navigateur ne la fournit pas toujours ; on la reconstitue alors depuis
   * le déplacement, en écartant les écarts inférieurs à la précision du
   * relevé, qui ne sont que du bruit.
   */
  #computeSpeed(
    raw: LocationSample,
    filtered: { lat: number; lon: number },
  ): number | null {
    if (raw.speedMs !== null && raw.speedMs > 0) {
      return raw.speedMs
    }

    const previous = this.#previous
    if (previous === null) return null

    const dtSeconds = (raw.timestamp - previous.timestamp) / 1000
    if (dtSeconds <= 0) return null

    const distance = haversine(
      previous.lat,
      previous.lon,
      filtered.lat,
      filtered.lon,
    )
    if (distance < raw.accuracy) return null

    return distance / dtSeconds
  }

  /**
   * Cap suivi par la carte.
   *
   * Gelé sous le seuil de vitesse : à l'arrêt, le cap GPS tourne au gré du
   * bruit et ferait pivoter la carte sans raison.
   */
  #computeBearing(
    raw: LocationSample,
    filtered: { lat: number; lon: number },
    speed: number | null,
  ): number | null {
    if (speed === null || speed <= BEARING_SPEED_THRESHOLD_MS) {
      return null
    }

    let candidate = raw.bearing

    if (candidate === null && this.#previous !== null) {
      const moved = haversine(
        this.#previous.lat,
        this.#previous.lon,
        filtered.lat,
        filtered.lon,
      )
      if (moved >= MIN_MOVE_DISTANCE_M) {
        candidate = bearingBetween(
          this.#previous.lat,
          this.#previous.lon,
          filtered.lat,
          filtered.lon,
        )
      }
    }

    if (candidate === null) return this.#bearing

    if (
      this.#bearing !== null &&
      Math.abs(normalizeBearingDelta(candidate - this.#bearing)) <
        MIN_BEARING_CHANGE_DEG
    ) {
      return this.#bearing
    }

    this.#bearing = candidate
    return candidate
  }

  #handleError(error: GeolocationPositionError): void {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        // Refus définitif : insister ne servirait qu'à vider la batterie.
        this.options.onError('permission_denied')
        this.stop()
        break

      case error.POSITION_UNAVAILABLE:
        // Signalé une seule fois : en zone difficile, l'erreur se répète à
        // chaque tentative et noierait l'écran de messages.
        if (!this.#reportedUnavailable) {
          this.#reportedUnavailable = true
          this.options.onError('unavailable')
        }
        break

      default:
        // Un dépassement de délai isolé est fréquent en couverture faible :
        // la surveillance reste active.
        break
    }
  }
}

function bearingBetween(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const toRad = Math.PI / 180
  const dLon = (lon2 - lon1) * toRad
  const y = Math.sin(dLon) * Math.cos(lat2 * toRad)
  const x =
    Math.cos(lat1 * toRad) * Math.sin(lat2 * toRad) -
    Math.sin(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.cos(dLon)

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}
